/**
 * Shared SSO Service for Demo Applications
 * Handles SSO token validation and authentication across subdomains
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.syberkey.com';
const SSO_PORTAL_URL = process.env.REACT_APP_SSO_PORTAL_URL || 'https://sso.syberkey.com';

interface SSOUser {
  userId: string;
  email: string;
  fullName?: string;
}

interface SSOSession {
  sessionId?: string;
  expiresAt: string;
  issuedAt: string;
}

interface SSOValidationResponse {
  success: boolean;
  valid: boolean;
  user: SSOUser;
  session: SSOSession;
}

class SSOService {
  /**
   * Validate if token is a valid JWT format
   */
  private isValidJWT(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  }

  /**
   * Get SSO token from URL parameter, cookie, or localStorage
   */
  private getSSOToken(): string | null {
    // First, check URL parameters (for localhost cross-port sharing)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('sso_token');
    if (urlToken) {
      // Validate token format before storing
      if (!this.isValidJWT(urlToken)) {
        console.error('[ssoService] Invalid JWT token received from URL - not a valid JWT format!');
        console.error('[ssoService] Token preview:', urlToken.substring(0, 50));
        console.error('[ssoService] Token parts:', urlToken.split('.').length);
        // Clean URL without storing invalid token
        const newUrl = window.location.pathname + (window.location.hash || '');
        window.history.replaceState({}, document.title, newUrl);
        return null;
      }
      
      // Store in localStorage for future use and remove from URL
      localStorage.setItem('sso_token', urlToken);
      localStorage.setItem('auth_token', urlToken);
      
      // Clear logout flag if it exists (new session is being established)
      if (localStorage.getItem('sso_logout_flag') === 'true') {
        console.log('[ssoService Sales] Clearing logout flag - new session established');
        localStorage.removeItem('sso_logout_flag');
      }
      
      // Clean URL without reload
      const newUrl = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, document.title, newUrl);
      return urlToken;
    }
    
    // Try to get from cookie (shared across subdomains in production)
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sso_token' && value) {
        if (!this.isValidJWT(value)) {
          console.error('[ssoService] Invalid JWT token in cookie - ignoring it');
          continue;
        }
        return value;
      }
    }
    
    // Fallback to localStorage
    const token = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
    if (token && !this.isValidJWT(token)) {
      console.error('[ssoService] Invalid JWT token in localStorage - clearing it');
      localStorage.removeItem('sso_token');
      localStorage.removeItem('auth_token');
      return null;
    }
    return token;
  }

  /**
   * Validate SSO token with backend
   */
  async validateToken(): Promise<SSOValidationResponse | null> {
    const token = this.getSSOToken();
    
    if (!token) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/sso/validate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const data: SSOValidationResponse = await response.json();
        console.log('[ssoService Sales] Token validation successful:', data.valid);
        console.log('[ssoService Sales] Session ID:', data.session?.sessionId);
        return data;
      } else {
        const errorText = await response.text();
        console.error('[ssoService Sales] Token validation failed with status:', response.status);
        console.error('[ssoService Sales] Error response:', errorText);
        
        // Check if it's a session invalidation (cross-app logout)
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error === 'session_invalidated' || errorData.crossAppLogout) {
            console.log('[ssoService Sales] Session invalidated - cross-app logout detected');
            // Set logout flag to notify other components
            this.setLogoutFlag();
          }
        } catch (e) {
          // Not JSON, ignore
        }
        
        // Token is invalid or expired
        this.clearToken();
        return null;
      }
    } catch (error) {
      console.error('SSO token validation failed:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const validation = await this.validateToken();
    return validation !== null && validation.valid === true;
  }

  /**
   * Get current user from SSO token
   */
  async getCurrentUser(): Promise<SSOUser | null> {
    const validation = await this.validateToken();
    return validation?.user || null;
  }

  /**
   * Redirect to SSO portal for login
   */
  redirectToSSO(returnUrl?: string): void {
    const currentUrl = returnUrl || window.location.href;
    window.location.href = `${SSO_PORTAL_URL}/login?redirect=${encodeURIComponent(currentUrl)}`;
  }

  /**
   * Clear SSO token
   */
  clearToken(): void {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!isLocalhost) {
      // Clear cookie for production
      document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
    }
    
    // Clear localStorage
    localStorage.removeItem('sso_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }

  /**
   * Check if logout was triggered from another tab/app
   * IMPORTANT: Only returns true if logout flag exists AND no valid token is present
   */
  checkLogoutFlag(): boolean {
    const logoutFlag = localStorage.getItem('sso_logout_flag');
    if (logoutFlag === 'true') {
      // Check if we have a valid token in URL or storage - if yes, clear the flag (false alarm)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('sso_token');
      const storageToken = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
      
      // If we have a token (URL or storage), this is a false alarm - clear the flag
      if (urlToken || storageToken) {
        const token = urlToken || storageToken;
        if (token && this.isValidJWT(token)) {
          console.log('[ssoService Sales] Logout flag found but valid token exists - clearing flag (false alarm)');
          localStorage.removeItem('sso_logout_flag');
          return false;
        }
      }
      
      console.log('[ssoService Sales] Logout flag detected - session invalidated from another app');
      return true;
    }
    return false;
  }

  /**
   * Set logout flag to notify other tabs/apps
   */
  setLogoutFlag(): void {
    localStorage.setItem('sso_logout_flag', 'true');
    // Remove flag after a short delay to allow other tabs to detect it
    setTimeout(() => {
      localStorage.removeItem('sso_logout_flag');
    }, 1000);
  }

  /**
   * Logout - Google-style cross-application logout
   * Invalidates all sessions for the user across all applications
   */
  async logout(): Promise<void> {
    console.log('[ssoService Sales] Logout initiated - Google-style cross-app logout');
    const token = this.getSSOToken();
    
    if (token) {
      try {
        console.log('[ssoService Sales] Calling logout API to invalidate all sessions...');
        const response = await fetch(`${API_BASE_URL}/sso/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token,
            allSessions: true // Always true for Google-style cross-app logout
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('[ssoService Sales] Logout API call successful');
          console.log('[ssoService Sales] Sessions invalidated:', result.sessionsInvalidated);
          console.log('[ssoService Sales] Cross-app logout:', result.crossAppLogout);
        } else {
          const errorText = await response.text();
          console.error('[ssoService Sales] Logout API call failed with status:', response.status);
          console.error('[ssoService Sales] Error response:', errorText);
        }
      } catch (error) {
        console.error('[ssoService Sales] Logout API call failed:', error);
        // Continue with local logout even if API call fails
      }
    }

    // Set logout flag to notify other tabs/apps immediately
    this.setLogoutFlag();
    
    // Clear tokens locally
    this.clearToken();
    
    // Broadcast logout event to other tabs/windows
    window.localStorage.setItem('sso_logout_event', Date.now().toString());
    window.localStorage.removeItem('sso_logout_event');
    
    console.log('[ssoService Sales] Redirecting to SSO portal...');
    window.location.href = `${SSO_PORTAL_URL}/`;
  }

  /**
   * Check session status (lightweight check for periodic validation)
   */
  async checkSessionStatus(): Promise<boolean> {
    const token = this.getSSOToken();
    if (!token) {
      return false;
    }

    try {
      // Decode token to get sessionId (if available)
      const parts = token.split('.');
      if (parts.length === 3) {
        try {
          const payload = JSON.parse(atob(parts[1]));
          const sessionId = payload.sessionId;
          
          if (sessionId) {
            // Lightweight session check
            const response = await fetch(`${API_BASE_URL}/sso/session/check?sessionId=${encodeURIComponent(sessionId)}`);
            if (response.ok) {
              const data = await response.json();
              return data.valid === true;
            }
          }
        } catch (e) {
          // If we can't decode token, fall back to full validation
        }
      }
      
      // Fallback to full token validation
      const validation = await this.validateToken();
      return validation !== null && validation.valid === true;
    } catch (error) {
      console.error('[ssoService Sales] Session status check failed:', error);
      return false;
    }
  }
}

export const ssoService = new SSOService();
export type { SSOUser, SSOSession, SSOValidationResponse };
